import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getSignedStorageUrl } from '@/features/agriagent/lib/signedAudio';
import { useUserStore } from '@/shared/state/userStore';
import i18n from '@/shared/i18n/config';
import { recordServerNow } from '@/shared/lib/serverClock';
import { validateSession } from '@/shared/lib/sessionCheck';
import { clearSnapshots } from '@/shared/lib/snapshot/store';
import { queryClient } from '@/shared/lib/queryClient';
import { clearPersistedCache } from '@/shared/lib/queryPersistence';

// FIX-SEASON-GATE (SD-T583): a session that ends — by Log Out or by the server refusing it — takes
// its query cache with it, in memory and in IndexedDB, so the next farmer on this phone never
// starts from the previous farmer's (or the previous session's) answers.
const clearQueryCache = () => {
  queryClient.clear();
  void clearPersistedCache();
};

interface AuthUser {
  id: string;
  phone?: string;
  sessionToken?: string;
  countryCode?: string;
  // Currency for all money surfaces, single-sourced from the farmer's
  // countries row (currency_code + currency_symbol) via profiles.country_id.
  currencyCode?: string;
  currencySymbol?: string;
  avatarUrl?: string;
}

export interface LoginWithPinResult {
  success: boolean;
  locked?: boolean;
  lockedUntil?: string;
  attemptsRemaining?: number;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithPin: (phone: string, pin: string) => Promise<LoginWithPinResult>;
  // WAP Phase 1 (P1.3): seed a session from a bridge-minted token (no PIN).
  // Produces the exact same authenticated state the PIN path does.
  loginWithToken: (token: string, userId: string) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
  getSessionToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper to fetch country code, avatar, and language for a user
const fetchUserProfileData = async (userId: string): Promise<{ countryCode: string | null; countryId: string | null; currencyCode: string | null; currencySymbol: string | null; avatarUrl: string | null; fullName: string | null; region: string | null; language: string | null }> => {
  try {
    const { data: profile } = await supabase
      .from('profiles')
      .select('country_id, avatar_url, full_name, region, language')
      .eq('id', userId)
      .single();

    let countryCode = null;
    let currencyCode = null;
    let currencySymbol = null;
    if (profile?.country_id) {
      // Single source of truth for currency: the farmer's countries row.
      const { data: country } = await supabase
        .from('countries')
        .select('code, currency_code, currency_symbol')
        .eq('id', profile.country_id)
        .single();

      countryCode = country?.code || null;
      currencyCode = country?.currency_code || null;
      currencySymbol = country?.currency_symbol || null;
    }

    return {
      countryCode,
      countryId: profile?.country_id || null,
      currencyCode,
      currencySymbol,
      // FIX-CCS-PRIVACY-COMPLETE: avatars is a PRIVATE bucket — sign the stored value (accepts a
      // legacy full public URL or a bare path) so avatars render app-wide.
      avatarUrl: (await getSignedStorageUrl(profile?.avatar_url, 'avatars')) || null,
      fullName: profile?.full_name || null,
      region: profile?.region || null,
      language: profile?.language || null,
    };
  } catch (error) {
    console.error('Failed to fetch user profile data:', error);
    return { countryCode: null, countryId: null, currencyCode: null, currencySymbol: null, avatarUrl: null, fullName: null, region: null, language: null };
  }
};

// Apply DB language to i18next if it differs. `profiles.language` is char(5)
// with trailing spaces — trim before compare/apply.
const syncI18nLanguage = async (dbLanguage: string | null | undefined) => {
  if (!dbLanguage) return;
  const lang = dbLanguage.trim();
  if (lang && lang !== i18n.language) {
    await i18n.changeLanguage(lang);
  }
};

// Session check: invalid only when the server says so (SD-437) — see src/lib/sessionCheck.ts

// Clear all session state and redirect to login
const clearSessionAndRedirect = () => {
  clearQueryCache();
  localStorage.removeItem('agxl_user');
  (window as any).__AGXL_SESSION_TOKEN__ = null;
  (window as any).__AGXL_USER_ID__ = null;
  window.location.href = '/auth';
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const keepaliveRef = useRef<number | null>(null);

  const { updateProfile } = useUserStore.getState();

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('agxl_user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);

        // Validate session token before restoring (skip if just logged in)
        if (parsed.sessionToken) {
          setSupabaseSessionHeader(parsed.sessionToken);

          const loginTime = parseInt(localStorage.getItem('agxl_login_time') || '0');
          const isRecentLogin = Date.now() - loginTime < 10000;

          if (!isRecentLogin) {
            const isValid = await validateSession(parsed.sessionToken);
            if (!isValid) {
              console.error('[AUTH] Stored session is invalid — redirecting to login');
              clearSessionAndRedirect();
              return;
            }
          }
        }

        setUser(parsed);
        // Set global user ID for other components
        (window as any).__AGXL_USER_ID__ = parsed.id;

        // Hydrate profile data on every restore — picks up language for i18n
        // sync (Defect α) and backfills country/avatar when missing locally.
        if (parsed.id) {
          const profileData = await fetchUserProfileData(parsed.id);

          // Defect α fix — align i18next with DB language so Lao farmers
          // who already have profiles.language='lo' see the Lao UI on restore.
          await syncI18nLanguage(profileData.language);

          // FIX-CCS-PRIVACY-COMPLETE (SD-AP5): the signed avatar URL expires (~1h). Because avatars
          // are a PRIVATE bucket, we must re-sign on read rather than reuse the cached signed link —
          // so always take the freshly-signed avatarUrl from this restore's fetch (never the stale
          // `parsed.avatarUrl`). Country/currency stay backfill-only (they don't expire).
          const updatedUser = {
            ...parsed,
            countryCode: parsed.countryCode || profileData.countryCode,
            currencyCode: parsed.currencyCode || profileData.currencyCode,
            currencySymbol: parsed.currencySymbol || profileData.currencySymbol,
            avatarUrl: profileData.avatarUrl ?? parsed.avatarUrl,
          };
          setUser(updatedUser);
          localStorage.setItem('agxl_user', JSON.stringify(updatedUser));

          // Also update Zustand store with profile data
          if (profileData.avatarUrl || profileData.fullName || profileData.region) {
            updateProfile({
              avatar_url: profileData.avatarUrl || undefined,
              full_name: profileData.fullName || undefined,
              region: profileData.region || undefined,
            });
          }
        }
      }
      setIsLoading(false);
    };
    
    initAuth();
  }, []);

  // Session keepalive — touch_session every 10 minutes
  useEffect(() => {
    if (user?.sessionToken) {
      keepaliveRef.current = window.setInterval(async () => {
        try {
          await supabase.rpc('touch_session', { session_token: user.sessionToken! });
        } catch (e) {
          console.error('[AUTH] Session keepalive failed:', e);
        }
      }, 10 * 60 * 1000);
    }
    return () => {
      if (keepaliveRef.current) clearInterval(keepaliveRef.current);
    };
  }, [user?.sessionToken]);

  // Re-validate session when coming back online
  useEffect(() => {
    const handleOnline = async () => {
      if (user?.sessionToken) {
        const isValid = await validateSession(user.sessionToken);
        if (!isValid) {
          console.error('[AUTH] Session invalid after reconnect — redirecting to login');
          clearSessionAndRedirect();
        }
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [user?.sessionToken]);

  const setSupabaseSessionHeader = (token: string) => {
    // Update Supabase client global headers
    (window as any).__AGXL_SESSION_TOKEN__ = token;
  };

  const loginWithPin = async (phone: string, pin: string): Promise<LoginWithPinResult> => {
    try {
      let normalizedPhone = phone.replace(/\s/g, '');
      if (!normalizedPhone.startsWith('+')) {
        normalizedPhone = '+856' + normalizedPhone.replace(/^0/, '');
      }

      const { data, error } = await supabase.rpc('verify_pin', {
        phone_input: normalizedPhone,
        pin_input: pin
      });

      if (error) {
        console.error('PIN verification error:', error);
        return { success: false };
      }

      // A2 (R17): every verify_pin row carries server_now — seed the clock gap at login.
      recordServerNow(Array.isArray(data) ? data[0]?.server_now : undefined, 'verify_pin');

      if (data && data.length > 0 && data[0].is_valid) {
        const userId = data[0].user_id;
        const sessionToken = data[0].session_token;
        
        // Set session header first so subsequent queries work
        setSupabaseSessionHeader(sessionToken);
        (window as any).__AGXL_USER_ID__ = userId;
        
        // Fetch profile data for the user
        const profileData = await fetchUserProfileData(userId);
        
        const authUser: AuthUser = {
          id: userId,
          phone: normalizedPhone,
          sessionToken: sessionToken,
          countryCode: profileData.countryCode || undefined,
          currencyCode: profileData.currencyCode || undefined,
          currencySymbol: profileData.currencySymbol || undefined,
          avatarUrl: profileData.avatarUrl || undefined,
        };
        
        setUser(authUser);
        localStorage.setItem('agxl_user', JSON.stringify(authUser));
        localStorage.setItem('agxl_login_time', Date.now().toString());
        if (profileData.countryId) {
          localStorage.setItem('agxl_country_id', profileData.countryId);
        }

        // Defect α fix — align i18next with DB language at login.
        await syncI18nLanguage(profileData.language);

        // Update Zustand store with profile data
        if (profileData.avatarUrl || profileData.fullName || profileData.region) {
          updateProfile({
            avatar_url: profileData.avatarUrl || undefined,
            full_name: profileData.fullName || undefined,
            region: profileData.region || undefined,
          });
        }
        return { success: true };
      }
      if (data && data.length > 0 && !data[0].is_valid) {
        const token = data[0].session_token as string | null;
        if (token && token.startsWith('LOCKED:')) {
          return { success: false, locked: true, lockedUntil: token.replace('LOCKED:', '') };
        }
        if (token && token.startsWith('ATTEMPTS:')) {
          const remaining = parseInt(token.replace('ATTEMPTS:', ''), 10);
          return { success: false, locked: false, attemptsRemaining: isNaN(remaining) ? undefined : remaining };
        }
      }
      return { success: false };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false };
    }
  };

  // WAP Phase 1 (P1.3) — seed a session from a bridge-exchanged token. Mirrors
  // the loginWithPin success branch exactly (minus phone + the verify_pin call),
  // so the resulting authenticated state is indistinguishable from a PIN login.
  const loginWithToken = async (token: string, userId: string): Promise<{ success: boolean }> => {
    try {
      if (!token || !userId) return { success: false };

      // Set session header first so subsequent queries authenticate.
      setSupabaseSessionHeader(token);
      (window as any).__AGXL_USER_ID__ = userId;

      const profileData = await fetchUserProfileData(userId);

      const authUser: AuthUser = {
        id: userId,
        sessionToken: token,
        countryCode: profileData.countryCode || undefined,
        currencyCode: profileData.currencyCode || undefined,
        currencySymbol: profileData.currencySymbol || undefined,
        avatarUrl: profileData.avatarUrl || undefined,
      };

      setUser(authUser);
      localStorage.setItem('agxl_user', JSON.stringify(authUser));
      localStorage.setItem('agxl_login_time', Date.now().toString());
      if (profileData.countryId) {
        localStorage.setItem('agxl_country_id', profileData.countryId);
      }

      // Align i18next with DB language, same as the PIN path.
      await syncI18nLanguage(profileData.language);

      if (profileData.avatarUrl || profileData.fullName || profileData.region) {
        updateProfile({
          avatar_url: profileData.avatarUrl || undefined,
          full_name: profileData.fullName || undefined,
          region: profileData.region || undefined,
        });
      }
      return { success: true };
    } catch (error) {
      console.error('Bridge token login failed:', error);
      return { success: false };
    }
  };

  const logout = async () => {
    clearQueryCache();
    const leaving = user?.id;
    setUser(null);
    localStorage.removeItem('agxl_user');
    (window as any).__AGXL_SESSION_TOKEN__ = null;
    (window as any).__AGXL_USER_ID__ = null;
    // P1 OD-SNAPSHOT: the farmer's saved screens leave with the farmer (a shared phone never shows the last farmer's data).
    if (leaving) await clearSnapshots(leaving).catch(() => 0);
  };

  const getSessionToken = () => {
    return user?.sessionToken || null;
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, isLoading, loginWithPin, loginWithToken, logout, getSessionToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
